import { useFields } from "@/api/hooks";
import type { CustomValues } from "@/api/types";
import { Spinner } from "@/components/ui";
import { CustomFieldInput } from "@/features/contacts/CustomFieldInput";

/** Renders every custom field defined for an object type. */
export function CustomFieldsFieldset({ objectType, value, onChange, errors }: {
  objectType: "contact" | "account" | "deal";
  value: CustomValues;
  onChange: (next: CustomValues) => void;
  errors: Record<string, string>;
}) {
  const fields = useFields(objectType);
  if (fields.isLoading) return <Spinner label="Loading fields" />;
  if (!fields.data?.length) return null;
  return (
    <fieldset className="rounded-lg border border-line p-4">
      <legend className="px-1 text-sm font-semibold">Additional details</legend>
      <div className="grid gap-4 sm:grid-cols-2">
        {fields.data.map((f) => (
          <CustomFieldInput key={f.id} field={f} value={value[f.key]} error={errors[`custom.${f.key}`]}
            onChange={(v) => onChange({ ...value, [f.key]: v as CustomValues[string] })} />
        ))}
      </div>
    </fieldset>
  );
}

/** Label for each custom field key, for timelines and detail pages. */
export function useFieldLabels(objectType: "contact" | "account" | "deal"): Record<string, string> {
  const fields = useFields(objectType);
  return Object.fromEntries((fields.data ?? []).map((f) => [`custom.${f.key}`, f.label]));
}

export function formatCustom(v: unknown): string | null {
  if (v === undefined || v === null || v === "") return null;
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}
